import { SaleModel } from 'api-hooks/sale/common';
import { SaleStatusEnum } from 'common/constants/enum';
import * as React from 'react';

export function getSaleStatus(status: SaleStatusEnum) {
  const defaultValues = {
    isDraft: false,
    isPending: false,
    isActive: false,
    isRejected: false,
    isCompleted: false,
    isRefunded: false,
    isVoid: false,
    isRescheduling: false,
    isWaiting: false,
  };

  switch (status) {
    case SaleStatusEnum.Active:
    case SaleStatusEnum.OpenTicket:
      return {
        ...defaultValues,
        isActive: true,
      };
    case SaleStatusEnum.Rescheduling:
      return {
        ...defaultValues,
        isRescheduling: true,
      };
    case SaleStatusEnum.PendingApproval:
    case SaleStatusEnum.PendingRescheduleApproval:
    case SaleStatusEnum.PendingVoidApproval:
    case SaleStatusEnum.PendingRevertVoidApproval:
      return {
        ...defaultValues,
        isPending: true,
      };
    case SaleStatusEnum.RescheduleDraft:
    case SaleStatusEnum.Draft:
    case SaleStatusEnum.VoidDraft:
    case SaleStatusEnum.RevertVoidDraft:
      return {
        ...defaultValues,
        isDraft: true,
      };
    case SaleStatusEnum.Completed:
      return {
        ...defaultValues,
        isCompleted: true,
      };
    case SaleStatusEnum.Waiting:
      return {
        ...defaultValues,
        isWaiting: true,
      };
    case SaleStatusEnum.RejectedApproval:
    case SaleStatusEnum.RejectedRescheduleApproval:
      return {
        ...defaultValues,
        isRejected: true,
      };
    case SaleStatusEnum.Void:
      return {
        ...defaultValues,
        isVoid: true,
      };
    case SaleStatusEnum.Rescheduled:
    case SaleStatusEnum.Refund:
    case SaleStatusEnum.Processed:
    case SaleStatusEnum.Submitted:
    case SaleStatusEnum.Archived:
      return defaultValues;
  }
}

export default function useSalesStatus(data: SaleModel) {
  const { status, isCompleted } = data;

  const buttonCondition = React.useMemo(() => {
    const defaultValues = {
      edit: false,
      addPurchase: false,
      viewTipping: false,
      reschedule: false,
      openTicket: false,
      refund: false,
      addToProject: false,
      void: false,
      duplicate: true, //all status can edit
      changeDocumentStatus: true, // all status can update document_status
      rejectApproval: false,
      saveInvoice: true,
    };
    switch (status) {
      case SaleStatusEnum.Active:
        return {
          ...defaultValues,
          viewTipping: true,
          addPurchase: !isCompleted,
          openTicket: true,
          addToProject: true,
          reschedule: true,
          void: true,
          edit: !isCompleted,
        };
      case SaleStatusEnum.Completed:
        return {
          ...defaultValues,
          viewTipping: true,
          addToProject: true,
          reschedule: true,
          refund: true,
          addPurchase: !isCompleted,
        };
      case SaleStatusEnum.OpenTicket:
        return {
          ...defaultValues,
          reschedule: true,
          openTicket: true,
          addToProject: true,
          addPurchase: !isCompleted,
          edit: !isCompleted,
        };

      case SaleStatusEnum.Rescheduled:
        return {
          ...defaultValues,
          reschedule: true,
          addPurchase: !isCompleted,
          addToProject: true,
        };
      case SaleStatusEnum.Refund:
        return {
          ...defaultValues,
          refund: true,
          addToProject: true,
          viewTipping: false,
        };
      case SaleStatusEnum.RejectedApproval:
      case SaleStatusEnum.RejectedRescheduleApproval:
        return {
          ...defaultValues,
          rejectApproval: true,
        };
      case SaleStatusEnum.Rescheduling:
      case SaleStatusEnum.PendingApproval:
      case SaleStatusEnum.PendingRescheduleApproval:
      case SaleStatusEnum.PendingVoidApproval:
      case SaleStatusEnum.RescheduleDraft:
      case SaleStatusEnum.Draft:
      case SaleStatusEnum.VoidDraft:
      case SaleStatusEnum.Void:
      case SaleStatusEnum.Waiting:
      case SaleStatusEnum.Processed:
      case SaleStatusEnum.Submitted:
      case SaleStatusEnum.Archived:
      default:
        return defaultValues;
    }
  }, [isCompleted, status]);

  const saleStatus = React.useMemo(() => {
    const _status = getSaleStatus(status);

    return {
      ..._status,
      isHideActionComponent: Object.keys(buttonCondition).every(
        (key) => !buttonCondition[key],
      ),
    };
  }, [buttonCondition, status]);

  return { saleStatus, buttonCondition };
}
